/**
 * @fileoverview Shared Tag schema used by both frontend and backend.
 * Tag has custom name validation (lowercase-hyphenated format).
 */

import { z } from 'zod';
import { commonValidators, createCrudSchemas, createEntityIdSchema, createdAtSchema, deletedAtSchema, getSchemaFields, updatedAtSchema } from './base.js';
import { ID_PATTERNS } from './types.js';

/**
 * Tag validation schema.
 * Unlike other displayable entities, tags have custom name validation
 * requiring lowercase letters and numbers separated by hyphens.
 */
export const TagSchema = z.object({
  id: createEntityIdSchema(ID_PATTERNS.TAG, 'ID must be in tag nanoid7 format (ta + digit 2-9 + 4 alphanumeric)'),
  name: z.string()
    .min(1, 'Name is required')
    .max(50, 'Name must be 50 characters or less')
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Tag name must be lowercase letters and numbers separated by hyphens')
    .trim(),
  description: commonValidators.optionalString(1000),
  display_order: commonValidators.displayOrder(),
  metadata: commonValidators.jsonField('Metadata'),
  deleted_at: deletedAtSchema,
  created_at: createdAtSchema,
  updated_at: updatedAtSchema,
});

const { CreateSchema, UpdateSchema } = createCrudSchemas(TagSchema);

export const TagCreateSchema = CreateSchema;
export const TagUpdateSchema = UpdateSchema;

export type Tag = z.infer<typeof TagSchema>;
export type TagCreate = z.infer<typeof TagCreateSchema>;
export type TagUpdate = z.infer<typeof TagUpdateSchema>;

export const TAG_FIELDS = getSchemaFields(TagSchema);
